import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BankQuestion } from '@proref/types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Icon } from '../shared/Icons';
import { Input } from '../shared/Input';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { Skeleton } from '../shared/Skeleton';
import { AnswerRow } from './AnswerRow';
import { apiErrorMessage } from '../../lib/api';
import { notifyError, notifySuccess } from '../../lib/toast';
import { useBankQuestionsQuery } from '../../hooks/use-question-bank';
import { useRulesQuery } from '../../hooks/use-sports';
import { useAddExamRuleQuestions } from '../../hooks/use-exams';

// a. b. c. d. — option labels for the stacked answers (matches the design).
const OPTION_LETTERS = 'abcdefgh'.split('');

function Select({ className = '', ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={
        'h-9 rounded-btn border-[1.5px] border-line bg-surface px-3 text-[13px] text-ink ' +
        'focus:border-accent focus:outline-none ' +
        className
      }
      {...props}
    />
  );
}

// One selectable row — a checkbox rather than an immediate Add, so several questions can be
// queued and submitted in a single batch. This is ClipPickerDialog's ClipCard for text instead of
// video: a list row (a question's content is a prompt + answers, not a poster) with the same
// checkbox-glyph-over-content selection affordance. A question already saved on the exam renders
// checked + disabled so it can't be double-added (matched on prompt text — the bank has no FK back
// from exam_questions, so text identity is the only link available).
function QuestionRow({
  question,
  selected,
  alreadyOn,
  onToggle,
}: {
  question: BankQuestion;
  selected: boolean;
  alreadyOn: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const checked = selected || alreadyOn;
  return (
    <button
      type="button"
      onClick={alreadyOn ? undefined : onToggle}
      aria-pressed={checked}
      disabled={alreadyOn}
      className={[
        'flex w-full items-start gap-3 rounded border p-3 text-left transition-colors',
        alreadyOn
          ? 'cursor-default border-line bg-bg-2 opacity-60'
          : checked
            ? 'border-accent bg-accent/[0.04]'
            : 'border-line-2 bg-surface hover:border-line-3 hover:bg-surface-2',
      ].join(' ')}
    >
      <span
        className={
          'mt-0.5 grid h-[20px] w-[20px] flex-none place-items-center rounded border ' +
          (checked ? 'border-accent bg-accent text-accent-ink' : 'border-line-3 bg-surface')
        }
      >
        {checked && <Icon.check className="h-3 w-3" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full border border-accent/30 bg-accent/10 px-2.5 py-0.5 font-mono text-[11px] font-medium text-accent">
            {t('common.ruleLabel', { number: question.rule_article_number })} · {question.rule_article_title}
          </span>
          {alreadyOn && (
            <span className="inline-flex items-center rounded-full bg-ink/85 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-bg">
              {t('admin.exams.clipPicker.onExam')}
            </span>
          )}
        </div>
        <p className="mt-2 text-[13px] text-ink">{question.question_text || t('admin.exams.editor.questionRow.defaultPrompt')}</p>
        {/* Answers listed one under the other, labelled a/b/c/d; the correct one is boxed + badged. */}
        <ol className="mt-2 flex flex-col gap-1.5">
          {question.options.map((opt, i) => (
            <AnswerRow key={opt} letter={OPTION_LETTERS[i]!} text={opt} correct={question.correct_answers.includes(opt)} />
          ))}
        </ol>
      </div>
    </button>
  );
}

interface QuestionPickerDialogProps {
  examId: string;
  sportId: string;
  sportSlug: string | undefined;
  // Prompts already saved on the exam — pre-checked + disabled so they can't be re-added.
  existingQuestionTexts: Set<string>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Multi-select question-bank picker — the rule-test counterpart to ClipPickerDialog's "build an
// exam from selected videos" flow. Lists the sport's reusable question bank as a searchable,
// filterable checklist; "Add N questions" posts the whole selection in one batch call, same as
// the clip picker (no local staging step — pick and it's on the exam). Self-contained: owns
// selection, the mutation, and its own toasts.
export function QuestionPickerDialog({
  examId,
  sportId,
  sportSlug,
  existingQuestionTexts,
  open,
  onOpenChange,
}: QuestionPickerDialogProps) {
  const { t } = useTranslation();
  const add = useAddExamRuleQuestions(examId);
  const [search, setSearch] = useState('');
  const [ruleId, setRuleId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const rulesQuery = useRulesQuery(sportSlug);

  // Only pass a search term once it's non-trivial, so every keystroke isn't a new query key.
  const trimmed = search.trim();
  const bankQuery = useBankQuestionsQuery(
    { sportId, ruleArticleId: ruleId || undefined, search: trimmed.length >= 2 ? trimmed : undefined },
    { enabled: open }, // don't fetch the bank until the picker is actually opened
  );

  const questions = bankQuery.data ?? [];
  // Client-side text filter for 1-char searches (below the server's 2-char threshold), so typing
  // feels responsive before the query kicks in.
  const filtered = useMemo(() => {
    if (trimmed.length !== 1) return questions;
    const q = trimmed.toLowerCase();
    return questions.filter((x) => (x.question_text ?? '').toLowerCase().includes(q));
  }, [questions, trimmed]);

  const byId = useMemo(() => new Map(questions.map((q) => [q.id, q])), [questions]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Reset selection + filters whenever the dialog closes, so re-opening starts clean.
  function handleOpenChange(next: boolean) {
    if (!next) {
      setSelected(new Set());
      setSearch('');
      setRuleId('');
    }
    onOpenChange(next);
  }

  async function confirm() {
    const picked = [...selected].map((id) => byId.get(id)).filter((q): q is BankQuestion => q !== undefined);
    if (picked.length === 0) return;
    try {
      const added = await add.mutateAsync({
        questions: picked.map((q) => ({
          rule_article_id: q.rule_article_id,
          question_text: q.question_text ?? undefined,
          options: q.options,
          correct_answers: q.correct_answers,
        })),
      });
      notifySuccess(t('admin.exams.questionPicker.addedToast', { count: added.length }));
      handleOpenChange(false);
    } catch (err) {
      notifyError(err, t('admin.exams.questionPicker.addErrorToast'));
    }
  }

  const count = selected.size;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Wider than a typical form dialog — a filterable list needs the room a compact form didn't. */}
      <DialogContent className="flex max-h-[85vh] w-[min(95vw,48rem)] max-w-none flex-col">
        <DialogHeader>
          <DialogTitle>{t('admin.exams.questionPicker.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.exams.questionPicker.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-1 flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Icon.search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('admin.exams.questionPicker.searchPlaceholder')}
              className="pl-9"
              aria-label={t('admin.exams.questionPicker.searchAria')}
            />
          </div>
          <Select value={ruleId} onChange={(e) => setRuleId(e.target.value)} aria-label={t('admin.exams.questionPicker.filterByRuleAria')}>
            <option value="">{t('admin.exams.questionPicker.allRuleArticles')}</option>
            {(rulesQuery.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.article_number} · {r.title}
              </option>
            ))}
          </Select>
        </div>

        {/* Scrollable list — the only part that grows, so the header + footer stay pinned. */}
        <div className="mt-3 min-h-0 flex-1 overflow-y-auto scroll-thin pr-1">
          {bankQuery.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : bankQuery.isError ? (
            <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
              {apiErrorMessage(bankQuery.error, t('admin.exams.questionPicker.couldNotLoad'))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded border border-dashed border-line-2 bg-bg-2 px-6 py-8 text-center text-[13px] text-muted">
              {(questions.length === 0 && !trimmed && !ruleId)
                ? t('admin.exams.questionPicker.emptyLibrary')
                : t('admin.exams.questionPicker.noMatches')}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {filtered.map((q) => (
                <QuestionRow
                  key={q.id}
                  question={q}
                  selected={selected.has(q.id)}
                  alreadyOn={q.question_text !== null && existingQuestionTexts.has(q.question_text)}
                  onToggle={() => toggle(q.id)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
          <span className="text-[12.5px] text-muted">
            {count === 0 ? t('admin.exams.questionPicker.noneSelected') : t('admin.exams.questionPicker.selectedCount', { count })}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => handleOpenChange(false)} disabled={add.isPending}>
              {t('common.cancel')}
            </Button>
            <Button variant="accent" size="sm" onClick={confirm} disabled={count === 0 || add.isPending}>
              {add.isPending ? (
                <Spinner className="w-4 h-4" />
              ) : count === 0 ? (
                t('admin.exams.questionPicker.addButtonEmpty')
              ) : (
                t('admin.exams.questionPicker.addButton', { count })
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
